import { animate } from "@theme/animation";
import { ThemeEvents } from "@theme/events";
import { prefersReducedMotion } from "@theme/utilities";

class PageTransition extends HTMLElement {
  constructor() {
    super();

    // Hard-refresh fallback: pjax-excluded navigations (checkout, account,
    // external) still trigger beforeunload — show the overlay to mask the unload.
    window.addEventListener("beforeunload", () => {
      document.body.classList.add("page-loading");
      this.#show();
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageTransitionStart, {
          bubbles: true,
          detail: { direction: "out", timestamp: Date.now() },
        })
      );
    });

    const initOnLoad = () => {
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageLoaded, {
          bubbles: true,
          detail: { timestamp: Date.now() },
        })
      );
      this.#hide({ announce: true });
    };

    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", initOnLoad, { once: true });
    } else {
      initOnLoad();
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        document.body.classList.remove("page-loading");
      }
    });

    // pjax-init.js dispatches start/end; we just animate.
    document.addEventListener(ThemeEvents.pageTransitionStart, () => this.#show());
    document.addEventListener(ThemeEvents.pageTransitionEnd, () => this.#hide());
  }

  #show() {
    this.hidden = false;
    const duration = prefersReducedMotion() ? 0 : 0.25;
    animate(this, { opacity: 1 }, { duration, allowWebkitAcceleration: true });
  }

  #hide({ announce = false } = {}) {
    const duration = prefersReducedMotion() ? 0 : 1;
    const finish = () => {
      this.hidden = true;
      if (announce) {
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.pageTransitionEnd, {
            bubbles: true,
            detail: { timestamp: Date.now() },
          })
        );
      }
    };
    animate(this, { opacity: 0 }, { duration, allowWebkitAcceleration: true })
      .finished.then(finish)
      .catch(finish);
  }
}

if (!customElements.get("page-transition")) {
  customElements.define("page-transition", PageTransition);
}
